import React from 'react';
import PropTypes from 'prop-types';
import MediaQuery from 'react-responsive';
import { useDispatch } from 'react-redux';

import { useTranslator, useUrlLocalizer } from 'tradera-lang/translate';
import { BREAKPOINT_SIZES, BREAKPOINTS } from 'tradera-constants/breakpoints';
import { useIsFeatureEnabled } from 'tradera-hooks/use-is-feature-enabled';
import GtmService from 'static/script/app/ui/google-tagmanager-service';
import { getMenuGaAttributes } from 'tradera-components/site-header/site-header.analytics';

import { useQualtive } from 'tradera-hooks/use-qualtive';
import { SURVEYS } from 'tradera-constants/surveys';
import uiActions from 'tradera-state/ui/actions';
import MenuItemSeparator from 'tradera-components/site-header/dropdown-menu-item-separator';
import MenuItem from 'tradera-components/site-header/dropdown-menu-item';
import ExpandableMenuItem from 'tradera-components/site-header/dropdown-menu-item-expandable';
import { LocalizationMenuItem } from './expandable-localization-menu-item';
import { CurrencyMenuItem } from './expandable-currency-menu-item';
import {
    InvoiceUnpaidAlert,
    InvoiceOverdueAlert,
    BankAccountMissingAlert,
    PayoutFailedAlert,
    PayoutReadyBankAccountMissingAlert,
    SwishMissingAlert,
    HyperwalletPayeeVerificationAlert,
    HyperwalletPayeeVerificationReviewAlert,
    OnboardToTraderaPayAlert,
    FeeNettingInfoAlert
} from './profile-menu-alerts';
import ProfileCard from '../profile/profile-card';
import AuctionIcon from '@tradera/blueprint/theme/icons2/auction.svg?jsx';
import AuctionLostIcon from '@tradera/blueprint/theme/icons2/bid-lost.svg?jsx';
import BookmarkIcon from '@tradera/blueprint/theme/icons2/bookmark.svg?jsx';
import BuyIcon from '@tradera/blueprint/theme/icons2/buy.svg?jsx';
import ChatIcon from '@tradera/blueprint/theme/icons2/chat.svg?jsx';
import DateIcon from '@tradera/blueprint/theme/icons2/date.svg?jsx';
import DocumentIcon from '@tradera/blueprint/theme/icons2/document.svg?jsx';
import HeartIcon from '@tradera/blueprint/theme/icons2/heart.svg?jsx';
import HomeIcon from '@tradera/blueprint/theme/icons2/home.svg?jsx';
import ListIcon from '@tradera/blueprint/theme/icons2/list.svg?jsx';
import LogoutIcon from '@tradera/blueprint/theme/icons2/logout.svg?jsx';
import PackageIcon from '@tradera/blueprint/theme/icons2/package.svg?jsx';
import PenIcon from '@tradera/blueprint/theme/icons2/pen.svg?jsx';
import RatingPositiveIcon from '@tradera/blueprint/theme/icons2/rating-positive.svg?jsx';
import SaleIcon from '@tradera/blueprint/theme/icons2/sale.svg?jsx';
import SettingsIcon from '@tradera/blueprint/theme/icons2/settings.svg?jsx';
import SoldIcon from '@tradera/blueprint/theme/icons2/sold.svg?jsx';
import StarIcon from '@tradera/blueprint/theme/icons2/star.svg?jsx';
import UnsoldIcon from '@tradera/blueprint/theme/icons2/unsold.svg?jsx';
import UserIcon from '@tradera/blueprint/theme/icons2/user.svg?jsx';
import WalletIcon from '@tradera/blueprint/theme/icons2/wallet.svg?jsx';
import WinningIcon from '@tradera/blueprint/theme/icons2/winning.svg?jsx';
import WriteIcon from '@tradera/blueprint/theme/icons2/write.svg?jsx';
import { useMenuHeightFix } from 'tradera-hooks/use-menu-height-fix';
import { localStore } from 'utils/storage';
import { STORAGE_KEYS } from 'tradera-constants/storage';
import initData from 'init-data';

const mediaBelowLarge = { maxWidth: BREAKPOINT_SIZES[BREAKPOINTS.MD].max };

const getGaAttributes = label =>
    getMenuGaAttributes('Top navigation', 'Account menu', label);

const trackOnClick = label => {
    GtmService.trackAction('Top navigation', 'Account menu', `Link - ${label}`);
};

const trackExpandItemClick = label => {
    GtmService.trackAction('Top navigation', 'Account menu', label);
};

const ProfileMenuContent = ({
    cleanedMemberAlias,
    dismissSwish,
    logOut,
    memberAlias,
    memberDetailedSellerRatingAverage,
    memberHeroImage = {},
    memberId,
    showMessagingLink,
    showPayments1Menu,
    showPayments2Menu,
    systemMessages,
    threadsUnread
}) => {
    const { t } = useTranslator();
    const dispatch = useDispatch();
    const { toLocalizedUrl } = useUrlLocalizer();
    const isTraderaShopEnabled = useIsFeatureEnabled('tradera-shop');
    const menuContentRef = useMenuHeightFix();
    const { isQualtiveEnabled, openQualtiveModal } = useQualtive();
    const isSellerListEnabled = useIsFeatureEnabled('my-tradera-seller-list');

    const closeProfileMenu = () => dispatch(uiActions.closeProfileMenu());

    return (
        <>
            <div className="profile-menu__profile-card-wrapper">
                <ProfileCard
                    closeProfileMenu={closeProfileMenu}
                    memberAlias={memberAlias}
                    memberId={memberId}
                    rating={memberDetailedSellerRatingAverage}
                    imageUrl={memberHeroImage?.min}
                    cleanedMemberAlias={cleanedMemberAlias}
                />
            </div>
            <ul className="plain-list" ref={menuContentRef}>
                <MenuItem
                    label={t('siteHeader_overview')}
                    link={toLocalizedUrl('/my/overview')}
                    className="site-dropdown__item-with-icon"
                    icon={<HomeIcon className="icon" />}
                    border
                    gaAttributes={getGaAttributes('My Tradera Overview')}
                />
                {showMessagingLink && (
                    <>
                        <MenuItem
                            label={t('siteHeader_messages')}
                            link={toLocalizedUrl('/messaging')}
                            className="site-dropdown__item-with-icon"
                            icon={<ChatIcon className="icon" />}
                            border
                            notify={threadsUnread}
                            follow={false}
                            gaAttributes={getGaAttributes('Messages')}
                        />
                    </>
                )}
                <MediaQuery {...mediaBelowLarge}>
                    <MenuItem
                        label={t('siteHeader_createItem')}
                        link={toLocalizedUrl('/selling')}
                        className="site-dropdown__item-with-icon"
                        icon={<PenIcon className="icon" />}
                        border
                        gaAttributes={getGaAttributes(
                            'Create new listing - from top'
                        )}
                    />
                    <MenuItemSeparator />
                    <ExpandableMenuItem
                        label={t('siteHeader_watchlist')}
                        className="site-dropdown__item-with-icon text-inter-medium"
                        icon={<HeartIcon className="icon" />}
                        border>
                        <MenuItem
                            label={t('siteHeader_wishlist')}
                            link={toLocalizedUrl('/my/wish-list')}
                            className="site-dropdown__item-with-icon indent-2"
                            icon={<HeartIcon className="icon" />}
                            border
                            gaAttributes={getGaAttributes('Wishlist')}
                        />
                        <MenuItem
                            label={t('siteHeader_savedSearches')}
                            link={toLocalizedUrl('/my/saved-searches')}
                            className="site-dropdown__item-with-icon indent-2"
                            icon={<BookmarkIcon className="icon" />}
                            border
                            gaAttributes={getGaAttributes('Saved searches')}
                        />

                        {isSellerListEnabled ? (
                            <MenuItem
                                label={t('siteHeader_sellerList')}
                                link={toLocalizedUrl('/my/seller-list')}
                                className="site-dropdown__item-with-icon indent-2"
                                icon={<StarIcon className="icon" />}
                                gaAttributes={getGaAttributes('Seller list')}
                            />
                        ) : (
                            <MenuItem
                                label={t('siteHeader_favouriteSellers')}
                                link={toLocalizedUrl('/my/favourite-sellers')}
                                className="site-dropdown__item-with-icon indent-2"
                                icon={<StarIcon className="icon" />}
                                gaAttributes={getGaAttributes(
                                    'Favorite seller'
                                )}
                            />
                        )}
                    </ExpandableMenuItem>
                    <ExpandableMenuItem
                        label={t('siteHeader_buying')}
                        className="site-dropdown__item-with-icon text-inter-medium"
                        icon={<BuyIcon className="icon" />}
                        border>
                        <MenuItem
                            label={t('siteHeader_ongoingAuctions')}
                            link={toLocalizedUrl('/my/active-items')}
                            className="site-dropdown__item-with-icon indent-2"
                            icon={<AuctionIcon className="icon" />}
                            border
                            gaAttributes={getGaAttributes('Ongoing auctions')}
                        />
                        <MenuItem
                            label={t('siteHeader_boughtItems')}
                            link={toLocalizedUrl('/my/purchases')}
                            className="site-dropdown__item-with-icon indent-2"
                            icon={<WinningIcon className="icon" />}
                            border
                            gaAttributes={getGaAttributes('Won / Bought')}
                        />
                        <MenuItem
                            label={t('siteHeader_lostAuctions')}
                            link={toLocalizedUrl('/my/items-lost')}
                            className="site-dropdown__item-with-icon indent-2"
                            icon={<AuctionLostIcon className="icon" />}
                            border
                            gaAttributes={getGaAttributes('Lost')}
                        />
                        <MenuItem
                            label={t('siteHeader_rateSeller')}
                            link={toLocalizedUrl(
                                '/my/leave-feedback/buyer?transactionId=0'
                            )}
                            className="site-dropdown__item-with-icon indent-2"
                            icon={<RatingPositiveIcon className="icon" />}
                            gaAttributes={getGaAttributes('Rate seller')}
                        />
                    </ExpandableMenuItem>
                    <ExpandableMenuItem
                        label={t('siteHeader_selling')}
                        className="site-dropdown__item-with-icon text-inter-medium"
                        icon={<SaleIcon className="icon" />}
                        border>
                        <MenuItem
                            label={t('siteHeader_activeItems')}
                            link={toLocalizedUrl('/my/active')}
                            className="site-dropdown__item-with-icon indent-2"
                            icon={<SaleIcon className="icon" />}
                            border
                            gaAttributes={getGaAttributes('Active listings')}
                        />
                        <MenuItem
                            label={t('siteHeader_soldItems')}
                            link={toLocalizedUrl('/my/sold')}
                            className="site-dropdown__item-with-icon indent-2"
                            icon={<SoldIcon className="icon" />}
                            border
                            gaAttributes={getGaAttributes('Sold listings')}
                        />
                        <MenuItem
                            label={t('siteHeader_unsoldItems')}
                            link={toLocalizedUrl('/my/unsold')}
                            className="site-dropdown__item-with-icon indent-2"
                            icon={<UnsoldIcon className="icon" />}
                            border
                            gaAttributes={getGaAttributes('Unsold listings')}
                        />
                        <MenuItem
                            label={t('siteHeader_picklist')}
                            link={toLocalizedUrl('/my/sold/picklist')}
                            className="site-dropdown__item-with-icon indent-2"
                            icon={<ListIcon className="icon" />}
                            border
                            gaAttributes={getGaAttributes('Picklist')}
                        />
                        <MenuItem
                            label={t('siteHeader_createItem')}
                            link={toLocalizedUrl('/selling')}
                            className="site-dropdown__item-with-icon indent-2"
                            icon={<PenIcon className="icon" />}
                            border
                            gaAttributes={getGaAttributes('Create new listing')}
                        />
                        <MenuItem
                            label={t('siteHeader_templates')}
                            link={toLocalizedUrl('/selling/templates')}
                            className="site-dropdown__item-with-icon indent-2"
                            icon={<WriteIcon className="icon" />}
                            border
                            gaAttributes={getGaAttributes('Listing templates')}
                        />
                        <MenuItem
                            label={t('siteHeader_rateBuyer')}
                            link={toLocalizedUrl(
                                '/my/leave-feedback/seller?transactionId=0'
                            )}
                            className="site-dropdown__item-with-icon indent-2"
                            icon={<RatingPositiveIcon className="icon" />}
                            border
                            gaAttributes={getGaAttributes('Rate buyer')}
                        />
                        {isTraderaShopEnabled && (
                            <MenuItem
                                label={t('siteHeader_buyShippingSupplies')}
                                link={toLocalizedUrl('/tradera-shop')}
                                className="site-dropdown__item-with-icon indent-2"
                                icon={<PackageIcon className="icon" />}
                                gaAttributes={getGaAttributes(
                                    'To Tradera Shop'
                                )}
                            />
                        )}
                    </ExpandableMenuItem>
                </MediaQuery>
                <MenuItemSeparator />
                <MenuItem
                    label={t('siteHeader_myProfile')}
                    link={toLocalizedUrl(
                        `/profile/items/${memberId}/${cleanedMemberAlias}`
                    )}
                    className="site-dropdown__item-with-icon"
                    icon={<UserIcon className="icon" />}
                    inSPA={true}
                    onClick={closeProfileMenu}
                    border
                    gaAttributes={getGaAttributes('My profile')}
                />
                <MenuItem
                    label={t('siteHeader_myRatings')}
                    link={toLocalizedUrl(
                        `/profile/feedback/${memberId}/${cleanedMemberAlias}`
                    )}
                    className="site-dropdown__item-with-icon"
                    icon={<RatingPositiveIcon className="icon" />}
                    inSPA={true}
                    onClick={closeProfileMenu}
                    border
                    gaAttributes={getGaAttributes('Ratings')}
                />
                <MenuItem
                    label={t('siteHeader_notifications')}
                    link={toLocalizedUrl('/my/notifications')}
                    className="site-dropdown__item-with-icon"
                    icon={<DateIcon className="icon" />}
                    border
                    gaAttributes={getGaAttributes('Notifications')}
                />
                <MenuItemSeparator />
                <MenuItem
                    label={
                        initData.isFeeNettingEnabled
                            ? t('siteheader_invoices2')
                            : t('siteheader_invoices')
                    }
                    link={toLocalizedUrl('/my/Invoices')}
                    className="site-dropdown__item-with-icon"
                    icon={<DocumentIcon className="icon" />}
                    notificationDot={systemMessages.InvoiceUnpaid && 'warning'}
                    alerts={[
                        systemMessages.InvoiceUnpaid && (
                            <InvoiceUnpaidAlert key={'InvoiceUnpaid'} />
                        ),
                        systemMessages.InvoiceOverdue && (
                            <InvoiceOverdueAlert key={'InvoiceOverdue'} />
                        )
                    ]}
                    border
                    gaAttributes={getGaAttributes('Invoices')}
                />
                {showPayments1Menu && (
                    <MenuItem
                        label={t('siteheader_payments')}
                        link={toLocalizedUrl('/my/Payments')}
                        className="site-dropdown__item-with-icon"
                        icon={<WalletIcon className="icon" />}
                        alerts={[
                            systemMessages.OnboardToTraderaPay && (
                                <OnboardToTraderaPayAlert key="OnboardToTraderaPay" />
                            )
                        ]}
                        border
                        gaAttributes={getGaAttributes('Payouts')}
                    />
                )}
                {showPayments2Menu && (
                    <MenuItem
                        label={
                            initData.isFeeNettingEnabled ? (
                                t('siteheader_paymentsAndFees')
                            ) : (
                                <>
                                    {t('siteheader_payments')}
                                    {!localStore.getItem(
                                        STORAGE_KEYS.BATCH_PAYOUTS_ALERT_DISMISSED
                                    ) && (
                                        <span className="badge badge-branded badge-inline ml-1">
                                            {t('siteHeader_updated')}
                                        </span>
                                    )}
                                </>
                            )
                        }
                        link={toLocalizedUrl('/my/payouts')}
                        className="site-dropdown__item-with-icon"
                        icon={<WalletIcon className="icon" />}
                        alerts={[
                            systemMessages.BankAccountMissing && (
                                <BankAccountMissingAlert
                                    key={'BankAccountMissing'}
                                />
                            ),
                            systemMessages.PayoutReadyBankAccountMissing && (
                                <PayoutReadyBankAccountMissingAlert
                                    key={'PayoutReadyBankAccountMissing'}
                                />
                            ),
                            systemMessages.PayoutFailed && (
                                <PayoutFailedAlert key={'PayoutFailed'} />
                            ),
                            systemMessages.SwishNumberMissing && (
                                <SwishMissingAlert
                                    key={'SwishNumberMissing'}
                                    enableSwishUrl={toLocalizedUrl(
                                        '/my/settings/payouts'
                                    )}
                                    dismissSwish={dismissSwish}
                                />
                            ),
                            systemMessages.HyperwalletPayeeVerificationRequired && (
                                <HyperwalletPayeeVerificationAlert key="HyperwalletPayeeVerification" />
                            ),
                            systemMessages.HyperwalletPayeeVerificationUnderReview && (
                                <HyperwalletPayeeVerificationReviewAlert key="HyperwalletPayeeVerificationReview" />
                            ),
                            systemMessages.FeeNettingInfo && (
                                <FeeNettingInfoAlert key="FeeNettingInfo" />
                            )
                        ]}
                        border
                        gaAttributes={getGaAttributes('Payouts')}
                    />
                )}
                <MenuItem
                    label={t('siteheader_settings')}
                    link={toLocalizedUrl('/my/settings')}
                    className="site-dropdown__item-with-icon"
                    icon={<SettingsIcon className="icon" />}
                    border
                    gaAttributes={getGaAttributes('Settings')}
                />
                <LocalizationMenuItem onToggle={trackExpandItemClick} />
                <CurrencyMenuItem onToggle={trackExpandItemClick} />
                <MenuItemSeparator />
                {isQualtiveEnabled && (
                    <MenuItem
                        label={t('siteheader_feedback')}
                        className="site-dropdown__item-with-icon"
                        icon={<ChatIcon className="icon" />}
                        onClick={() => {
                            openQualtiveModal(SURVEYS.general, t);
                            trackOnClick('Leave feedback Qualtive');
                        }}
                    />
                )}
                <MenuItem
                    label={t('siteheader_customerService')}
                    link="https://info.tradera.com/"
                    className="site-dropdown__item-with-icon"
                    icon={<ChatIcon className="icon" />}
                    border
                    gaAttributes={getGaAttributes('Customer service')}
                />
                <MenuItemSeparator />
                <MenuItem
                    label={t('siteHeader_logOut')}
                    onClick={logOut}
                    link={toLocalizedUrl('/login/logout')}
                    className="site-dropdown__item-with-icon"
                    icon={<LogoutIcon className="icon" />}
                    gaAttributes={getGaAttributes('Logout')}
                />
                {/* NOTE: Fix for iPhone menu making bottom element unclickable */}
                <MenuItemSeparator className="site-dropdown__separator--bottom d-lg-none" />
            </ul>
        </>
    );
};

ProfileMenuContent.propTypes = {
    cleanedMemberAlias: PropTypes.string,
    dismissSwish: PropTypes.func,
    logOut: PropTypes.func,
    memberAlias: PropTypes.string,
    memberDetailedSellerRatingAverage: PropTypes.number,
    memberHeroImage: PropTypes.object,
    memberId: PropTypes.number,
    showMessagingLink: PropTypes.bool,
    showPayments1Menu: PropTypes.bool,
    showPayments2Menu: PropTypes.bool,
    systemMessages: PropTypes.object,
    threadsUnread: PropTypes.number
};
ProfileMenuContent.defaultProps = {
    systemMessages: {}
};

export default ProfileMenuContent;
